import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { usePermissionRestriction } from "./usePermissionRestriction";

import { checkCameraOrMicPermission } from "..";

import {
  fetchRTCTokenAgora,
  fetchRTMTokenAgora,
  setCallingDetails,
  setChannelName,
  setIsVideoCallInitiated,
  setShowOutgoingCall,
  setVideoConsulationTitle,
  videoAppointmentDetails,
} from "@/store/slices/videoAppointment";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import useCallNotificationApi from "./useCallNotificationApi";
import { storage } from "../Storage";

import { IRoleType } from "@/models/role";
import { ISessionKeys } from "@/models";
import {
  IAgoraTokenForVideoCallPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";

export const useVideoCallStartFromDoctorJRDoctor = () => {
  const [initiateVideoCallLoading, setInitiateVideoCallLoading] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const appointmentDetail = useAppSelector(
    (state) => state.appointment.appointmentDetail
  );

  const { appointmentDetail: videoAppointmentDetail, videoConsulationTitle } =
    useAppSelector(videoAppointmentDetails);

  const sessionKey = [
    ISessionKeys.ROLE_TYPE,
    ISessionKeys.NAME,
    ISessionKeys.DOCTOR_NAME,
  ];

  const [roleType, name, doctorName] = sessionKey.map((key) =>
    storage.getSessionToken(key)
  );

  const permissionRestrictionHandler = usePermissionRestriction();
  const { handleIncomingCallNotification } = useCallNotificationApi();

  const initiateVideoCallHandler = async () => {
    const isNotificationPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.VIDEO_CALL,
      IAgoraTokenForVideoCallPermissionEnums.SEND_VIDEO_CALL_NOTIFICATION
    );
    if (!isNotificationPermissionGranted) return;

    const permissionAvailable = await checkCameraOrMicPermission();
    if (!permissionAvailable) return;

    dispatch(setShowOutgoingCall(true));
    setInitiateVideoCallLoading(true);

    const isRTMTokenPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.VIDEO_CALL,
      IAgoraTokenForVideoCallPermissionEnums.GENERATE_VIDEO_CALL_TOKEN_FOR_WEB
    );
    if (!isRTMTokenPermissionGranted) return;

    await dispatch(fetchRTMTokenAgora(appointmentDetail?.id));

    const isRTCTokenPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.VIDEO_CALL,
      IAgoraTokenForVideoCallPermissionEnums.GENERATE_VIDEO_CALL_TOKEN_FOR_APP
    );
    if (!isRTCTokenPermissionGranted) return;

    await dispatch(fetchRTCTokenAgora(appointmentDetail?.id));

    dispatch(setChannelName(appointmentDetail?.id));
    dispatch(setIsVideoCallInitiated(true));

    dispatch(
      setCallingDetails({
        id: appointmentDetail?.id,
        callerName: name ?? "Unknown",
        callingName: appointmentDetail?.patient?.name,
        callingId: appointmentDetail?.patient?.patientId,
        age: appointmentDetail?.patient?.age,
        gender: appointmentDetail?.patient?.gender,
      })
    );

    dispatch(
      setVideoConsulationTitle({
        doctorName: roleType === IRoleType.DOCTOR ? name : doctorName,
        patientName: videoAppointmentDetail?.patient?.name,
        patientId: videoAppointmentDetail?.patient?.patientId,
      })
    );

    await handleIncomingCallNotification(
      appointmentDetail?.id,
      name ?? "Unknown",
      appointmentDetail?.patient?.name,
      appointmentDetail?.patient?.patientId,
      appointmentDetail?.patient?.memberId ??
        videoAppointmentDetail?.patient?.memberId,
      roleType === IRoleType.DOCTOR
        ? name
        : roleType === IRoleType.PATIENT
        ? videoConsulationTitle?.doctorName
        : doctorName,
      roleType === IRoleType.PATIENT
        ? videoConsulationTitle?.patientName
        : videoAppointmentDetail?.patient?.name,
      roleType === IRoleType.PATIENT
        ? videoConsulationTitle?.patientId
        : videoAppointmentDetail?.patient?.patientId
    );

    navigate("/join-room");
  };

  return { initiateVideoCallHandler, initiateVideoCallLoading };
};
